import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Search, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import BlurredValue from '../../../components/common/BlurredValue';
import ReportPreviewModal from '../../../components/reports/ReportPreviewModal';
import { formatDateToISO } from '../../../utils/formatters';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const WholesaleSalesBookPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [totalSales, setTotalSales] = useState(0);
  const [totalNet, setTotalNet] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [filters, setFilters] = useState({
    startDate: formatDateToISO(new Date()),
    endDate: formatDateToISO(new Date()),
    search: '',
    status: ''
  });

  useEffect(() => {
    fetchOrders();
  }, [filters.startDate, filters.endDate, filters.status]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/wholesale/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          search: filters.search,
          status: filters.status
        }
      });

      const ordersData = response.data?.data || [];
      setOrders(ordersData);

      // Calculate totals
      const total = ordersData.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
      const tax = ordersData.reduce((sum, order) => sum + parseFloat(order.tax_amount || 0), 0);
      const net = total - tax;

      setTotalSales(total);
      setTotalTax(tax);
      setTotalNet(net);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load wholesale sales book');
    } finally {
      setLoading(false);
    }
  };

  const setPresetRange = (range) => {
    const end = new Date();
    let start = new Date();

    switch (range) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setDate(start.getDate() - 30);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        break;
    }

    setFilters((prev) => ({
      ...prev,
      startDate: formatDateToISO(start),
      endDate: formatDateToISO(end)
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDateRangeLabel = () => {
    const start = new Date(filters.startDate).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const end = new Date(filters.endDate).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    return `${start} - ${end}`;
  };

  const ReportContent = () => (
    <div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Orders</p>
          <p className="text-lg font-bold">{orders.length}</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Net Value</p>
          <p className="text-lg font-bold">{formatCurrency(totalNet)}</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Total Tax</p>
          <p className="text-lg font-bold">{formatCurrency(totalTax)}</p>
        </div>
        <div className="text-center p-3 bg-emerald-50 rounded-lg border-2 border-emerald-200">
          <p className="text-xs text-emerald-600 mb-1">Total Sales</p>
          <p className="text-lg font-bold text-emerald-700">{formatCurrency(totalSales)}</p>
        </div>
      </div>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-3 py-2 text-left">Date</th>
            <th className="border border-gray-300 px-3 py-2 text-left">Order No</th>
            <th className="border border-gray-300 px-3 py-2 text-left">Customer</th>
            <th className="border border-gray-300 px-3 py-2 text-right">Net</th>
            <th className="border border-gray-300 px-3 py-2 text-right">Tax</th>
            <th className="border border-gray-300 px-3 py-2 text-right">Total</th>
            <th className="border border-gray-300 px-3 py-2 text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td className="border border-gray-300 px-3 py-2">{formatDate(order.order_date)}</td>
              <td className="border border-gray-300 px-3 py-2 font-medium">{order.order_number}</td>
              <td className="border border-gray-300 px-3 py-2">{order.business_name}</td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                {formatCurrency(parseFloat(order.total_amount) - parseFloat(order.tax_amount || 0))}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                {formatCurrency(order.tax_amount)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right font-medium">
                {formatCurrency(order.total_amount)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-center capitalize">
                {order.status}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/wholesale/reports')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Wholesale Sales Book</h1>
                <p className="text-gray-500 text-sm mt-1">
                  Detailed ledger of all wholesale orders
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowReportModal(true)}
                disabled={loading || orders.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                <FileText className="w-4 h-4" /> Generate Report
              </button>
              <button
                onClick={fetchOrders}
                className="p-2 border rounded-lg hover:bg-gray-50 text-gray-600"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-col lg:flex-row gap-4">
            <div className="flex flex-wrap gap-2">
              {['today', 'yesterday', 'week', 'month', 'year'].map((range) => (
                <button
                  key={range}
                  onClick={() => setPresetRange(range)}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md capitalize"
                >
                  {range}
                </button>
              ))}
            </div>
            <div className="flex-1 flex flex-col sm:flex-row gap-4 items-center">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex-1 w-full relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search order #, customer..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-emerald-100">
            <p className="text-gray-500 text-sm">Total Wholesale Value</p>
            <p className="text-2xl font-bold text-gray-900">
              <BlurredValue value={formatCurrency(totalSales)} />
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
            <p className="text-gray-500 text-sm">Net Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              <BlurredValue value={formatCurrency(totalNet)} />
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-100">
            <p className="text-gray-500 text-sm">Tax Collected</p>
            <p className="text-2xl font-bold text-gray-900">
              <BlurredValue value={formatCurrency(totalTax)} />
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-emerald-100">
            <p className="text-gray-500 text-sm">Order Count</p>
            <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Order No</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3 text-right">Net</th>
                  <th className="px-4 py-3 text-right">Tax</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(order.order_date)}</td>
                    <td className="px-4 py-3 font-medium text-emerald-600">{order.order_number}</td>
                    <td className="px-4 py-3">{order.business_name}</td>
                    <td className="px-4 py-3 text-right">
                      <BlurredValue
                        value={formatCurrency(
                          parseFloat(order.total_amount) - parseFloat(order.tax_amount || 0)
                        )}
                      />
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      <BlurredValue value={formatCurrency(order.tax_amount)} />
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      <BlurredValue value={formatCurrency(order.total_amount)} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : order.status === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ReportPreviewModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Wholesale Sales Book Report"
        subtitle={getDateRangeLabel()}
      >
        <ReportContent />
      </ReportPreviewModal>
    </div>
  );
};

export default WholesaleSalesBookPage;
